import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchNextBlockPage, fetchPoolInfo, ApiError } from '../services/mempoolApi.js';
import { analyzePoolPerformance, generatePredictionData, detectOutliers, calculatePoolBlockRateFromPoolInfo, backtestAccuracy, calculateEmpiricalLambda, optimizeModelParams } from '../utils/poissonCalculations.js';

const PoolDataContext = createContext(null);

// Default settings
const DEFAULT_SETTINGS = {
  analysisWindowHours: 24,
  hashrateWeight: 0.6,
  blockShareWeight: 0.4,
  confidenceLevel: 0.9,
  outlierPThreshold: 0.05,  // Default 5% tails
  maxIterations: 3,         // Default outlier detection iterations
  autoOptimize: false,      // Auto-run optimization on load/settings change
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('bloracle-settings');
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_SETTINGS, ...parsed };
    }
  } catch (e) {
    console.warn('Failed to load settings from localStorage:', e);
  }
  return DEFAULT_SETTINGS;
};

// Save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem('bloracle-settings', JSON.stringify(settings));
  } catch (e) {
    console.warn('Failed to save settings to localStorage:', e);
  }
};

export function PoolDataProvider({ children }) {
  const [poolSlug, setPoolSlug] = useState('innopolistech');
  const [blocks, setBlocks] = useState([]);
  const [blocksMeta, setBlocksMeta] = useState({
    totalFetched: 0,
    hasMore: true,
    currentPage: 0,
    isLoadingMore: false,
    lastMinHeight: null,
  });
  const [poolInfo, setPoolInfo] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [predictionData, setPredictionData] = useState(null);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  const [settings, setSettings] = useState(loadSettings);
  const [filteredBlocks, setFilteredBlocks] = useState([]);
  const [outliersCount, setOutliersCount] = useState(0);
  const [outlierDetectionResult, setOutlierDetectionResult] = useState(null);
  const [backtestResults, setBacktestResults] = useState(null);
  const [optimizationResults, setOptimizationResults] = useState(null);
  const lastAutoOptimizationRef = useRef({ blocksLength: 0, poolInfoHash: null, settingsHash: null });

  // Get current time in UTC+3
  const getCurrentTimeUTC3 = () => {
    const now = new Date();
    const utc3Offset = 3 * 60 * 60; // UTC+3 in seconds
    return Math.floor(now.getTime() / 1000) + utc3Offset;
  };

  const fetchData = useCallback(async (forceRefresh = false) => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch only the first page initially (lazy loading)
      // Subsequent pages can be loaded via loadMoreBlocks()
      const [blocksResult, poolInfoData] = await Promise.all([
        fetchNextBlockPage(poolSlug, null, 1000, { forceRefresh }),
        fetchPoolInfo(poolSlug, { forceRefresh }),
      ]);

      const blocksData = blocksResult.blocks;
      setBlocks(blocksData);
      
      // Update blocksMeta with pagination state from first page
      setBlocksMeta(prev => ({
        ...prev,
        totalFetched: blocksResult.blocks.length,
        hasMore: blocksResult.hasMore,
        currentPage: 1,
        lastMinHeight: blocksResult.nextMinHeight,
      }));
      
      // If poolInfo has no hashrate data, try to estimate from blocks
      let poolInfoToUse = poolInfoData;
      if (poolInfoData.hashrateShare === 0 && poolInfoData.estimatedHashrate === 0 && blocksData.length > 0) {
        console.warn('⚠️ Pool info has no hashrate data. Attempting to estimate from blocks...');
        
        // Calculate estimated hashrate share from block count over available time period
        // Find the time range of available blocks
        const blockTimestamps = blocksData.map(b => b.timestamp).filter(ts => ts > 0);
        if (blockTimestamps.length > 0) {
          const oldestBlock = Math.min(...blockTimestamps);
          const newestBlock = Math.max(...blockTimestamps);
          const timeRangeSeconds = newestBlock - oldestBlock;
          const timeRangeDays = timeRangeSeconds / 86400;
          
          console.log('Block time range:', {
            oldestBlock: new Date(oldestBlock * 1000).toISOString(),
            newestBlock: new Date(newestBlock * 1000).toISOString(),
            timeRangeDays: timeRangeDays.toFixed(2),
            totalBlocks: blocksData.length,
          });
          
          // Estimate hashrate share: (blocks_mined / time_days) / 144 blocks_per_day
          // This gives us the average daily block rate, divided by network rate
          if (timeRangeDays > 0) {
            const blocksPerDay = blocksData.length / timeRangeDays;
            const estimatedShare = blocksPerDay / 144; // Network mines ~144 blocks/day
            
            console.log('Estimated hashrate share from blocks:', {
              blocksPerDay: blocksPerDay.toFixed(2),
              estimatedShare: estimatedShare.toFixed(6),
              estimatedSharePercent: (estimatedShare * 100).toFixed(4),
            });
            
            if (estimatedShare > 0 && estimatedShare <= 1) {
              poolInfoToUse = {
                ...poolInfoData,
                hashrateShare: estimatedShare,
                hashrateSharePercent: estimatedShare * 100,
                blocksMined24h: Math.round(blocksPerDay),
                name: poolInfoData.name || poolSlug,
                slug: poolInfoData.slug || poolSlug,
              };
              console.log('✅ Using estimated hashrate share:', estimatedShare);
            } else {
              console.warn('⚠️ Estimated share is invalid:', estimatedShare);
            }
          }
        }
      }
      
      setPoolInfo(poolInfoToUse);

      // Perform outlier detection
      let enrichedBlocks = blocksData;
      let filteredBlocksData = blocksData;
      let outliersCountValue = 0;
      let outlierResult = null;
      
      try {
        // Compute initial lambda for outlier detection
        const initialLambda = calculatePoolBlockRateFromPoolInfo(
          poolInfoToUse,
          settings.hashrateWeight,
          settings.blockShareWeight
        );
        
        if (initialLambda > 0 && blocksData.length > 1) {
          // Call detectOutliers with settings values
          outlierResult = detectOutliers(blocksData, initialLambda, settings.outlierPThreshold, settings.maxIterations);
          enrichedBlocks = outlierResult.blocks;
          filteredBlocksData = outlierResult.blocks.filter(b => !b.isOutlier);
          outliersCountValue = outlierResult.outliersCount;
          
          console.log('Outlier detection results:', {
            totalBlocks: blocksData.length,
            outliersCount: outliersCountValue,
            refinedLambda: outlierResult.refinedLambda,
            initialLambda: initialLambda,
            iterations: outlierResult.iterations,
          });
        } else {
          console.warn('Skipping outlier detection:', {
            initialLambda,
            blocksCount: blocksData.length,
          });
          // Mark all blocks as non-outliers
          enrichedBlocks = blocksData.map(block => ({ ...block, isOutlier: false }));
          filteredBlocksData = enrichedBlocks;
        }
      } catch (err) {
        console.warn('Outlier detection failed, using all blocks:', err);
        // Fallback: mark all blocks as non-outliers
        enrichedBlocks = blocksData.map(block => ({ ...block, isOutlier: false }));
        filteredBlocksData = enrichedBlocks;
      }
      
      // Ensure filteredBlocks is never empty (if all blocks are outliers, use original)
      if (filteredBlocksData.length === 0 && blocksData.length > 0) {
        console.warn('All blocks marked as outliers, using original blocks for analysis');
        filteredBlocksData = blocksData.map(block => ({ ...block, isOutlier: false }));
        // Update enrichedBlocks to match filteredBlocksData for consistency
        enrichedBlocks = filteredBlocksData;
      }
      
      setBlocks(enrichedBlocks);
      setFilteredBlocks(filteredBlocksData);
      setOutliersCount(outliersCountValue);
      setOutlierDetectionResult(outlierResult);

      // Debug logging
      console.log('=== PoolDataContext: Data Fetched ===');
      console.log('Pool slug:', poolSlug);
      console.log('Blocks count:', blocksData.length);
      console.log('Blocks sample (first 3):', blocksData.slice(0, 3).map(b => ({
        height: b.height,
        timestamp: b.timestamp,
        timestampDate: new Date(b.timestamp * 1000).toISOString(),
      })));
      console.log('PoolInfo (original):', {
        name: poolInfoData.name,
        slug: poolInfoData.slug,
        hashrateShare: poolInfoData.hashrateShare,
        estimatedHashrate: poolInfoData.estimatedHashrate,
      });
      console.log('PoolInfo (using):', {
        name: poolInfoToUse.name,
        slug: poolInfoToUse.slug,
        hashrateShare: poolInfoToUse.hashrateShare,
        hashrateSharePercent: poolInfoToUse.hashrateSharePercent,
        hashrateEH: poolInfoToUse.hashrateEH,
        estimatedHashrate: poolInfoToUse.estimatedHashrate,
        blocksMined24h: poolInfoToUse.blocksMined24h,
        lastBlockTimestamp: poolInfoToUse.lastBlockTimestamp,
        lastBlockTimestampDate: poolInfoToUse.lastBlockTimestamp ? new Date(poolInfoToUse.lastBlockTimestamp * 1000).toISOString() : 'N/A',
      });

      // Compute analysis using current time (UTC+3) and settings
      // Use filteredBlocks (non-outliers only) for analysis
      // Pass useEmpiricalLambda=true to use empirical lambda for predictions
      const currentTime = getCurrentTimeUTC3();
      const analysisResult = analyzePoolPerformance(
        filteredBlocksData, 
        poolInfoToUse, 
        settings.analysisWindowHours,
        settings.hashrateWeight,
        settings.blockShareWeight,
        true // useEmpiricalLambda=true (default)
      );
      
      // Log lambda comparison
      const lambdaDiff = analysisResult.lambdaEmpirical - analysisResult.lambdaHashrate;
      const lambdaDiffPercent = analysisResult.lambdaHashrate > 0 
        ? (lambdaDiff / analysisResult.lambdaHashrate) * 100 
        : 0;
      
      console.log('Analysis result summary:', {
        observedBlocks: analysisResult.observedBlocks,
        expectedBlocks: analysisResult.expectedBlocks,
        lambdaEmpirical: analysisResult.lambdaEmpirical,
        lambdaHashrate: analysisResult.lambdaHashrate,
        lambdaUsed: analysisResult.lambdaUsed,
        lambdaDiff,
        lambdaDiffPercent: lambdaDiffPercent.toFixed(2) + '%',
        empiricalStats: analysisResult.empiricalStats,
        expectedTime: analysisResult.expectedTime,
        medianTime: analysisResult.medianTime,
        expectedTimeHours: analysisResult.expectedTime / 3600,
        isInfinity: !Number.isFinite(analysisResult.expectedTime),
      });
      setAnalysis(analysisResult);

      // Perform backtesting to validate model accuracy
      let backtestResult = null;
      try {
        if (filteredBlocksData.length >= 2) {
          // Define lambda function that computes empirical lambda from prior blocks only
          const lambdaFunc = (priorBlocks) => {
            if (priorBlocks.length < 2) return null;
            const empiricalStats = calculateEmpiricalLambda(priorBlocks);
            return empiricalStats?.lambda || null;
          };
          
          backtestResult = backtestAccuracy(filteredBlocksData, lambdaFunc);
          
          console.log('Backtesting results:', {
            mae: backtestResult?.metrics.mae,
            rmse: backtestResult?.metrics.rmse,
            calibration: backtestResult?.metrics.calibration,
            totalPredictions: backtestResult?.metrics.totalPredictions,
            errorsCount: backtestResult?.errors.length,
          });
        } else {
          console.warn('Insufficient blocks for backtesting:', filteredBlocksData.length);
        }
      } catch (err) {
        console.warn('Backtesting failed:', err);
      }
      
      setBacktestResults(backtestResult);

      // Generate prediction curves with settings
      // Use filteredBlocks (non-outliers only) for predictions
      const predictionResult = generatePredictionData(
        filteredBlocksData, 
        poolInfoToUse, 
        currentTime, 
        settings.analysisWindowHours,
        settings.hashrateWeight,
        settings.blockShareWeight,
        settings.confidenceLevel
      );
      console.log('Prediction data summary:', {
        probabilityCurveLength: predictionResult.probabilityCurve?.length || 0,
        cumulativeCurveLength: predictionResult.cumulativeCurve?.length || 0,
        deviationBandsUpperLength: predictionResult.deviationBands?.upper?.length || 0,
        deviationBandsLowerLength: predictionResult.deviationBands?.lower?.length || 0,
        nextBlockProbabilities: predictionResult.nextBlockProbabilities,
        timeSinceLastBlock: predictionResult.timeSinceLastBlock,
      });
      console.log('=====================================');
      setPredictionData(predictionResult);

      setLastRefresh(new Date());
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err);
      } else {
        setError(new Error(err.message || 'Failed to fetch pool data'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [poolSlug, settings.analysisWindowHours, settings.hashrateWeight, settings.blockShareWeight, settings.confidenceLevel, settings.outlierPThreshold, settings.maxIterations]);

  const refreshData = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  /**
   * Load more blocks on demand (lazy loading)
   * Called when user scrolls to bottom or clicks "Load More" button
   * @returns {Promise<void>}
   */
  const loadMoreBlocks = useCallback(async () => {
    // Check if we can load more
    if (!blocksMeta.hasMore || blocksMeta.isLoadingMore) {
      return;
    }

    setBlocksMeta(prev => ({ ...prev, isLoadingMore: true }));

    try {
      const result = await fetchNextBlockPage(
        poolSlug,
        blocksMeta.lastMinHeight,
        1000,
        { forceRefresh: false }
      );

      // Append new blocks to existing blocks array
      // Note: Using blocks from closure is safe here since we're updating state immediately
      setBlocks(prev => {
        const updatedBlocks = [...prev, ...result.blocks];
        
        // Re-run outlier detection on the full combined blocks array
        if (poolInfo && updatedBlocks.length > 1) {
          try {
            const initialLambda = calculatePoolBlockRateFromPoolInfo(
              poolInfo,
              settings.hashrateWeight,
              settings.blockShareWeight
            );
            
            if (initialLambda > 0) {
              const outlierResult = detectOutliers(updatedBlocks, initialLambda, settings.outlierPThreshold, settings.maxIterations);
              let enrichedBlocks = outlierResult.blocks;
              const filteredBlocksData = outlierResult.blocks.filter(b => !b.isOutlier);
              
              // Ensure filteredBlocks is never empty
              const finalFilteredBlocks = filteredBlocksData.length > 0 
                ? filteredBlocksData 
                : updatedBlocks.map(block => ({ ...block, isOutlier: false }));
              
              // If all blocks were outliers, update enrichedBlocks to match finalFilteredBlocks
              if (filteredBlocksData.length === 0) {
                enrichedBlocks = finalFilteredBlocks;
              }
              
              // Update outlier-related state
              setFilteredBlocks(finalFilteredBlocks);
              setOutliersCount(outlierResult.outliersCount);
              setOutlierDetectionResult(outlierResult);
              
              // Return enriched blocks
              return enrichedBlocks;
            }
          } catch (err) {
            console.warn('Outlier detection failed during loadMoreBlocks:', err);
            // Fallback: mark all blocks as non-outliers
            const enrichedBlocks = updatedBlocks.map(block => ({ ...block, isOutlier: false }));
            setFilteredBlocks(enrichedBlocks);
            return enrichedBlocks;
          }
        } else {
          // No poolInfo or insufficient blocks, mark all as non-outliers
          const enrichedBlocks = updatedBlocks.map(block => ({ ...block, isOutlier: false }));
          setFilteredBlocks(enrichedBlocks);
          return enrichedBlocks;
        }
        
        return updatedBlocks;
      });

      // Update blocksMeta with new values
      setBlocksMeta(prev => ({
        ...prev,
        currentPage: prev.currentPage + 1,
        totalFetched: prev.totalFetched + result.blocks.length,
        hasMore: result.hasMore,
        lastMinHeight: result.nextMinHeight,
        isLoadingMore: false,
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err : new Error(err.message || 'Failed to load more blocks'));
      setBlocksMeta(prev => ({ ...prev, isLoadingMore: false }));
    }
  }, [poolSlug, blocksMeta.hasMore, blocksMeta.isLoadingMore, blocksMeta.lastMinHeight, poolInfo, settings.hashrateWeight, settings.blockShareWeight, settings.outlierPThreshold, settings.maxIterations]);

  // Update settings and trigger data refresh
  const updateSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
    // Trigger data refresh to apply new settings
    fetchData(true);
  }, [fetchData]);

  /**
   * Run parameter optimization to find best outlierPThreshold and hashrateWeight
   * @returns {Promise<Object|null>} Optimization results or null if failed
   */
  const runOptimization = useCallback(async () => {
    if (!blocks || blocks.length < 10 || !poolInfo) {
      console.warn('Insufficient data for optimization');
      return null;
    }
    
    setIsLoading(true);
    try {
      // Define candidate parameter grid
      const candidateParams = {
        outlierPThresholds: [0.01, 0.02, 0.03, 0.04, 0.05, 0.06, 0.07, 0.08, 0.09, 0.1],
        hashrateWeights: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      };
      
      // Run optimization (may take 5-10 seconds)
      const result = optimizeModelParams(blocks, poolInfo, candidateParams, {
        maxIterations: 3,
        progressCallback: (current, total, bestSoFar) => {
          console.log(`Optimization progress: ${current}/${total}, best MAE so far: ${bestSoFar?.toFixed(2)}s`);
        },
      });
      
      if (result && result.bestParams) {
        console.log('Optimization complete:', result);
        setOptimizationResults(result);
        
        // Auto-apply best parameters to settings
        const newSettings = {
          ...settings,
          outlierPThreshold: result.bestParams.outlierPThreshold,
          hashrateWeight: result.bestParams.hashrateWeight,
          blockShareWeight: 1 - result.bestParams.hashrateWeight,
        };
        updateSettings(newSettings);
        
        return result;
      } else {
        console.warn('Optimization failed to find valid parameters');
        return null;
      }
    } catch (err) {
      console.error('Optimization error:', err);
      setError(new Error('Parameter optimization failed: ' + err.message));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [blocks, poolInfo, settings, updateSettings]);

  // Initial load on mount
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Automatic polling with visibility API support
  useEffect(() => {
    if (!isPollingEnabled) return;

    const handleVisibilityChange = () => {
      // Polling will be controlled by visibility state in the interval
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    const pollInterval = setInterval(() => {
      // Only poll if tab is visible
      if (!document.hidden) {
        fetchData(true);
      }
    }, 60000); // 60 seconds

    // Cleanup on unmount or when polling is disabled
    return () => {
      clearInterval(pollInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, isPollingEnabled]);

  // Automatic optimization on load/settings change
  useEffect(() => {
    // Only run if auto-optimization is enabled
    if (!settings.autoOptimize) {
      return;
    }

    // Guard: ensure we have sufficient data
    if (!blocks || blocks.length < 10 || !poolInfo) {
      return;
    }

    // Create a signature for current data state to detect meaningful changes
    const blocksLength = blocks.length;
    const poolInfoHash = poolInfo ? `${poolInfo.slug}-${poolInfo.hashrateShare}` : null;
    // Track relevant settings that should trigger re-optimization
    const settingsHash = `${settings.outlierPThreshold}-${settings.hashrateWeight}-${settings.maxIterations}`;

    // Check if we've already run optimization for this data/settings combination
    const lastRun = lastAutoOptimizationRef.current;
    if (
      lastRun.blocksLength === blocksLength &&
      lastRun.poolInfoHash === poolInfoHash &&
      lastRun.settingsHash === settingsHash
    ) {
      // No meaningful change, skip optimization
      return;
    }

    // Debounce: only run optimization once when data becomes available
    // Use a timeout to prevent excessive re-runs
    const timeoutId = setTimeout(() => {
      console.log('Auto-optimizing parameters...');
      runOptimization().then(() => {
        // Update ref after successful optimization
        lastAutoOptimizationRef.current = {
          blocksLength,
          poolInfoHash,
          settingsHash,
        };
      });
    }, 1000); // 1 second debounce

    return () => {
      clearTimeout(timeoutId);
    };
  }, [blocks, poolInfo, settings.autoOptimize, settings.outlierPThreshold, settings.hashrateWeight, settings.maxIterations, runOptimization]);

  const contextValue = {
    poolSlug,
    blocks,
    blocksMeta,
    poolInfo,
    analysis,
    predictionData,
    selectedBlocks,
    isLoading,
    error,
    lastRefresh,
    setSelectedBlocks,
    refreshData,
    setPoolSlug,
    isPollingEnabled,
    setIsPollingEnabled,
    settings,
    updateSettings,
    loadMoreBlocks,
    filteredBlocks,
    outliersCount,
    outlierDetectionResult,
    backtestResults,
    optimizationResults,
    runOptimization,
  };

  return (
    <PoolDataContext.Provider value={contextValue}>
      {children}
    </PoolDataContext.Provider>
  );
}

export default PoolDataContext;

